<script setup lang="ts">
import RepairSection from '@/components/rc/miner-repairs/repairs-sidebar/RepairSection.vue'
import { useMinerRepairActions } from '@/composables/rc/miner-repair-actions'
import { useStatuses } from '@/composables/rc/statuses'
import { DiagnosticResult } from '@/types/rc/miners'
import { MinerStatus } from '@/types/rc/miners/MinerStatus'
import { useMinerDetails } from '@/composables/rc/miner-details'
import { computed, ref, toRefs, watchEffect } from 'vue'

const props = defineProps<{ minerId: number }>()

const { minerId } = toRefs(props)
const { minerDetailsQuery } = useMinerDetails(minerId)
const { data: minerDetails } = minerDetailsQuery

// Server state bindings

const minerStatus = computed<MinerStatus>(() => minerDetails.value?.status as MinerStatus)

// UI state

const selectedPartStatus = ref<MinerStatus | undefined>(minerStatus.value)

watchEffect(() => {
  selectedPartStatus.value = minerStatus.value
})

const { minerStatuses } = useStatuses() // Options

// Actions
const { updateMinerStatusMutation } = useMinerRepairActions(minerId)

const onSetStatus = () => {
  if (!selectedPartStatus.value) return
  updateMinerStatusMutation.mutate(
    { status: selectedPartStatus.value },
    {
      onError: () => {
        selectedPartStatus.value = minerStatus.value
      },
    }
  )
}

const isActive = computed(() => {
  if (!minerDetails.value?.status) {
    return false
  }

  switch (minerDetails.value?.status) {
    case MinerStatus.Cleaned:
      return true
    case MinerStatus.RepairAuthDenied:
      return true
    case MinerStatus.Completed:
      return true
    case MinerStatus.Diagnosed:
      return minerDetails.value?.condition.diagnostic?.result == DiagnosticResult.PASS
    default:
      return false
  }
})
</script>

<template>
  <RepairSection title="Repair" :step="3" :active="isActive">
    <div class="w-full">
      <p class="text-text-tertiary">Miner status</p>
      <el-select v-model="selectedPartStatus" @change="onSetStatus">
        <el-option v-for="status in minerStatuses" :key="status" :value="status">{{ status }}</el-option>
      </el-select>
    </div>
  </RepairSection>
</template>
